// ============================================================================
// EmployeeGrid (L05) — grille salariale « lignes par employe » (509 L PoC)
// ============================================================================
// Port FIDELE du PoC components/salary/employee-grid.tsx (509 L @ SHA 7e40e4a).
// Remplace le STUB de SL-B (meme signature de props EXACTE — zero dette, le board
// ne change pas). Grille partagee directeur (interactive) et CE (lecture seule) :
// par classe, bareme annuel + taux horaire en tete, puis une rangee par employe
// avec ses marqueurs par periode a chaque echelon (passe / reel / scenario).
//
// En mode interactif : menu « ... » par ligne (LineMenu), rangee « + Ajouter un
// employe » par classe (employe simule), icone historique (backfill), « + » qui
// deplie le passe dans les autres classes. Marqueur HG (hors grille) : ligne NON
// deplaçable (M-655 (a) applique cote board via `onCellClick`/isMoved ; ici les
// cellules d'une ligne offGrid sont inertes — `clickable` false).
//
// TRANSPOSITION (PLAN-SOUS-LOTS §SL-C, PLAN-ATTAQUE) :
//  - Badge @/components/ui → Badge (tone) de ../employee/ui, + `title` (a11y) ;
//    variant PoC → tone : neutral/info/warning inchanges, default → brand ;
//  - Button/Input @/components/ui → boutons natifs + input compact dark-aware ;
//  - tokens shadcn (bg-muted/text-muted-foreground/border-border/bg-card) → slate ;
//  - formatCurrency (fr-CA fige) → formatCad(locale) ; HOURLY fr-CA fige →
//    formatHourly(locale) (P14, helpers format.ts) ;
//  - text-[11px]/text-xs litteraux → echelle nommee text-chip/text-meta (DS-014) ;
//  - libelles FR en dur → cles i18n FR+EN (P13). Regle #4 zero emoji ; Regle #28
//    tokens dark-aware, zero CSS en dur cote couleur.

import { useMemo, useState } from 'react';
import { CalendarClock, History, Minus, Plus } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { cn } from '../../lib/utils';
import { Badge } from '../employee/ui';
import { formatCad, formatHourly } from './format';
import { ClassInfoButton } from './ClassInfoButton';
import { LineMenu } from './LineMenu';
import type { EmployeeGridLine, PeriodMarker } from '../../lib/salary/positions';
import { positionAnnual, positionHourly } from '../../lib/salary/scenario';
import type { SalaryClass } from '../../lib/types/firestore';

// Bouton icone compact (motif partage — remplace `Button variant=ghost size=5`).
const iconBtn =
  'inline-flex size-5 items-center justify-center rounded-md text-slate-400 transition-colors ' +
  'hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300 touch-manipulation';

// Input inline compact (remplace `Input h-6 w-36`).
const compactInput =
  'h-6 w-36 rounded-lg border border-slate-200 bg-white px-1.5 text-meta text-slate-800 ' +
  'focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 ' +
  'dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100';

/**
 * Props VERBATIM du PoC `EmployeeGrid` (contrat fige SL-B ↔ SL-C — la signature
 * ne change pas, le stub disparait). Toute la surface V2 est optionnelle : la vue
 * CE (lecture seule) rend exactement comme avant.
 */
export interface EmployeeGridProps {
  classes: SalaryClass[];
  linesByClass: Record<string, EmployeeGridLine[]>;
  currentYear: number;
  interactive?: boolean;
  onCellClick?: (line: EmployeeGridLine, classId: string, echelon: number) => void;
  onBackfillRequest?: (line: EmployeeGridLine) => void;
  onAddVirtual?: (classId: string, echelon: number, name: string) => void;
  onMoveToClass?: (line: EmployeeGridLine, targetClassId: string) => void;
  onRemoveScenario?: (line: EmployeeGridLine) => void;
  onRenameVirtual?: (lineId: string, name: string) => void;
  // Impact annuel des periodes d'heures speciales par email ($). Negatif = 0 h.
  specialHoursByEmail?: Record<string, number>;
}

export function EmployeeGrid({
  classes,
  linesByClass,
  currentYear,
  interactive = false,
  onCellClick,
  onBackfillRequest,
  onAddVirtual,
  onMoveToClass,
  onRemoveScenario,
  onRenameVirtual,
  specialHoursByEmail,
}: EmployeeGridProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Rangee d'ajout active (une seule a la fois) + nom en cours de saisie.
  const [addingClassId, setAddingClassId] = useState<string | null>(null);
  const [addName, setAddName] = useState('');
  // Renommage inline d'un fictif.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Membres ayant au moins une ligne hors de leur classe courante → « + ».
  const idsWithOtherLines = useMemo(() => {
    const ids = new Set<string>();
    for (const lines of Object.values(linesByClass)) {
      for (const l of lines) {
        if (!l.isCurrentClass && !l.virtual) ids.add(l.id);
      }
    }
    return ids;
  }, [linesByClass]);

  // Employes portant un marqueur scenario quelque part (pour « Retirer du
  // scenario » dans le menu, meme depuis la ligne de la classe courante).
  const idsWithScenario = useMemo(() => {
    const ids = new Set<string>();
    for (const lines of Object.values(linesByClass)) {
      for (const l of lines) {
        if (l.hasScenario) ids.add(l.id);
      }
    }
    return ids;
  }, [linesByClass]);

  function startAdding(classId: string) {
    setAddingClassId(classId);
    setAddName('');
  }

  function cancelAdding() {
    setAddingClassId(null);
    setAddName('');
  }

  function placeNew(classId: string, echelon: number) {
    if (!onAddVirtual) return;
    onAddVirtual(classId, echelon, addName);
    cancelAdding();
  }

  function commitRename(lineId: string) {
    if (onRenameVirtual && renameValue.trim()) {
      onRenameVirtual(lineId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isLineVisible = (l: EmployeeGridLine) =>
    l.isCurrentClass || l.hasScenario || expanded.has(l.id);

  return (
    <div className="flex flex-col gap-6">
      {classes.map((cls) => {
        const lines = (linesByClass[cls.id] ?? []).filter(isLineVisible);
        return (
          <div key={cls.id} className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <h4 className="text-field font-semibold text-slate-800 dark:text-slate-100">{cls.label}</h4>
              <ClassInfoButton description={cls.description} niveaux={cls.niveaux} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse whitespace-nowrap text-meta">
                <thead>
                  <tr className="bg-slate-100/70 uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                    <th className="px-2 py-1.5 text-left font-medium">
                      {t('gest:planification.grid.echelonHeader')}
                    </th>
                    {cls.echelons.map((e) => (
                      <th key={e.echelon} className="px-2 py-1.5 text-center font-medium">
                        {e.echelon}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-200 dark:border-slate-700">
                    <th className="px-2 py-1.5 text-left font-medium text-slate-500 dark:text-slate-400">
                      {t('gest:planification.grid.annualRow', { year: currentYear })}
                    </th>
                    {cls.echelons.map((e) => {
                      const annual = positionAnnual(cls.id, e.echelon, classes, currentYear);
                      const hourly = positionHourly(cls.id, e.echelon, classes, currentYear);
                      return (
                        <td
                          key={e.echelon}
                          className="px-2 py-1.5 text-center tabular-nums"
                          title={
                            hourly !== null
                              ? t('gest:planification.grid.hourlyTooltip', {
                                  rate: formatHourly(hourly, locale),
                                  hours: cls.base_hours_per_week,
                                })
                              : undefined
                          }
                        >
                          {annual !== null ? formatCad(annual, locale) : '—'}
                        </td>
                      );
                    })}
                  </tr>

                  <tr className="border-t border-slate-200 dark:border-slate-700">
                    <th className="px-2 py-1.5 text-left text-chip font-medium text-slate-500 dark:text-slate-400">
                      {t('gest:planification.grid.hourlyRow', { year: currentYear })}
                    </th>
                    {cls.echelons.map((e) => {
                      const hourly = positionHourly(cls.id, e.echelon, classes, currentYear);
                      return (
                        <td
                          key={e.echelon}
                          className="px-2 py-1.5 text-center text-chip tabular-nums text-slate-500 dark:text-slate-400"
                        >
                          {hourly !== null ? formatHourly(hourly, locale) : '—'}
                        </td>
                      );
                    })}
                  </tr>

                  {lines.map((line) => (
                    <tr
                      key={line.id}
                      className={cn(
                        'border-t border-slate-200 dark:border-slate-700',
                        // Vue budget/planification : inactifs ombrages.
                        !line.active && 'opacity-60',
                      )}
                    >
                      <th
                        scope="row"
                        className={cn(
                          'px-2 py-1.5 text-left font-medium',
                          line.isCurrentClass && !line.virtual
                            ? 'text-slate-800 dark:text-slate-100'
                            : 'text-slate-500 dark:text-slate-400',
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {renamingId === line.id ? (
                            <input
                              autoFocus
                              value={renameValue}
                              aria-label={t('gest:planification.grid.renameAria', { name: line.name })}
                              className={compactInput}
                              onChange={(ev) => setRenameValue(ev.target.value)}
                              onBlur={() => commitRename(line.id)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter') commitRename(line.id);
                                if (ev.key === 'Escape') {
                                  setRenamingId(null);
                                  setRenameValue('');
                                }
                              }}
                            />
                          ) : (
                            <span
                              className={cn(
                                'max-w-40 truncate',
                                // Barre = ligne de PASSE dans une autre classe ; une
                                // ligne scenario (deplacement en cours) ne l'est pas.
                                !line.isCurrentClass &&
                                  !line.hasScenario &&
                                  'text-slate-500 line-through dark:text-slate-400',
                              )}
                              title={line.name}
                            >
                              {line.name}
                            </span>
                          )}
                          {line.niveau != null ? (
                            <Badge
                              tone="neutral"
                              title={
                                cls.niveaux.find((n) => n.level === line.niveau)?.label ??
                                t('gest:planification.classInfo.niveau', { level: line.niveau })
                              }
                            >
                              N{line.niveau}
                            </Badge>
                          ) : null}
                          {line.virtual ? (
                            <Badge tone="info">{t('gest:planification.grid.fictifBadge')}</Badge>
                          ) : null}
                          {(() => {
                            // Marqueur heures speciales (incl. mises a pied 0 h) sur la
                            // ligne reelle — montre que le cout reel differe du bareme.
                            if (!line.isCurrentClass || line.virtual || !line.email) return null;
                            const sh = specialHoursByEmail?.[line.email] ?? 0;
                            if (sh === 0) return null;
                            const isReduction = sh < 0;
                            return (
                              <Badge
                                tone={isReduction ? 'neutral' : 'warning'}
                                title={
                                  isReduction
                                    ? t('gest:planification.grid.reductionTitle', {
                                        amount: formatCad(sh, locale),
                                      })
                                    : t('gest:planification.grid.surcoutTitle', {
                                        amount: formatCad(sh, locale),
                                      })
                                }
                              >
                                <CalendarClock size={12} aria-hidden="true" />
                                {isReduction
                                  ? t('gest:planification.grid.layoffBadge')
                                  : t('gest:planification.grid.specialBadge')}
                              </Badge>
                            );
                          })()}
                          {line.isCurrentClass && idsWithOtherLines.has(line.id) ? (
                            <button
                              type="button"
                              className={iconBtn}
                              aria-expanded={expanded.has(line.id)}
                              aria-label={
                                expanded.has(line.id)
                                  ? t('gest:planification.grid.collapseAria', { name: line.name })
                                  : t('gest:planification.grid.expandAria', { name: line.name })
                              }
                              onClick={() => toggleExpanded(line.id)}
                            >
                              {expanded.has(line.id) ? (
                                <Minus size={14} aria-hidden="true" />
                              ) : (
                                <Plus size={14} aria-hidden="true" />
                              )}
                            </button>
                          ) : null}
                          {interactive && onBackfillRequest && line.isCurrentClass && !line.virtual ? (
                            <button
                              type="button"
                              className={iconBtn}
                              aria-label={t('gest:planification.grid.backfillAria', { name: line.name })}
                              title={t('gest:planification.grid.backfillTitle')}
                              onClick={() => onBackfillRequest(line)}
                            >
                              <History size={14} aria-hidden="true" />
                            </button>
                          ) : null}
                          {line.offGrid ? (
                            <Badge tone="warning" title={t('gest:planification.grid.hgLineTitle')}>
                              {t('gest:planification.grid.hgBadge')}
                            </Badge>
                          ) : null}
                          {interactive &&
                          (onMoveToClass || onRemoveScenario) &&
                          line.isCurrentClass &&
                          !line.offGrid ? (
                            <LineMenu
                              line={line}
                              classes={classes}
                              hasAnyScenario={idsWithScenario.has(line.id)}
                              onMoveToClass={onMoveToClass}
                              onRemoveScenario={onRemoveScenario}
                              onRenameRequest={
                                onRenameVirtual
                                  ? (l) => {
                                      setRenamingId(l.id);
                                      setRenameValue(l.name);
                                    }
                                  : undefined
                              }
                            />
                          ) : null}
                        </span>
                      </th>
                      {cls.echelons.map((e) => {
                        const markers = line.markers[e.echelon] ?? [];
                        // Hors grille : ligne NON deplaçable (sa paie suit le taux
                        // reel, pas une position d'echelon) — cellules inertes.
                        const clickable = interactive && !!onCellClick && !line.offGrid;
                        const content = markers.length ? (
                          <span className="inline-flex flex-wrap items-center justify-center gap-0.5">
                            {markers.map((m, i) => (
                              <MarkerBadge key={i} marker={m} virtual={line.virtual} />
                            ))}
                          </span>
                        ) : null;
                        return (
                          <td key={e.echelon} className="px-1 py-1 text-center align-middle">
                            {clickable ? (
                              <button
                                type="button"
                                onClick={() => onCellClick(line, cls.id, e.echelon)}
                                aria-label={t('gest:planification.grid.placeCellAria', {
                                  name: line.name,
                                  echelon: e.echelon,
                                  class: cls.label,
                                })}
                                className={cn(
                                  'min-h-6 w-full rounded-md px-1 py-0.5 transition-colors hover:bg-amber-100/70 dark:hover:bg-amber-900/30',
                                  !markers.length && 'text-transparent',
                                )}
                              >
                                {content ?? '·'}
                              </button>
                            ) : (
                              content
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Rangee d'ajout d'un employe simule (scenario) */}
                  {interactive && onAddVirtual ? (
                    <tr className="border-t border-slate-200 dark:border-slate-700">
                      {addingClassId === cls.id ? (
                        <>
                          <th scope="row" className="px-2 py-1 text-left font-medium">
                            <span className="inline-flex items-center gap-1">
                              <input
                                autoFocus
                                value={addName}
                                placeholder={t('gest:planification.grid.addNamePlaceholder')}
                                aria-label={t('gest:planification.grid.addNameAria')}
                                className={compactInput}
                                onChange={(ev) => setAddName(ev.target.value)}
                                onKeyDown={(ev) => {
                                  if (ev.key === 'Escape') cancelAdding();
                                }}
                              />
                              <button
                                type="button"
                                className={iconBtn}
                                aria-label={t('gest:planification.grid.cancelAdd')}
                                onClick={cancelAdding}
                              >
                                <Minus size={14} aria-hidden="true" />
                              </button>
                            </span>
                          </th>
                          {cls.echelons.map((e) => (
                            <td key={e.echelon} className="px-1 py-1 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => placeNew(cls.id, e.echelon)}
                                aria-label={t('gest:planification.grid.placeNewAria', {
                                  echelon: e.echelon,
                                  class: cls.label,
                                })}
                                className="min-h-6 w-full rounded-md border border-dashed border-slate-300/60 px-1 py-0.5 transition-colors hover:border-blue-400/40 hover:bg-blue-100/70 dark:border-slate-600/60 dark:hover:bg-blue-900/30"
                              >
                                <Plus size={12} aria-hidden="true" className="mx-auto text-slate-400" />
                              </button>
                            </td>
                          ))}
                        </>
                      ) : (
                        <td colSpan={cls.echelons.length + 1} className="px-1 py-1">
                          <button
                            type="button"
                            className="inline-flex h-6 w-fit touch-manipulation items-center gap-1.5 rounded-md px-2 text-meta text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                            onClick={() => startAdding(cls.id)}
                          >
                            <Plus size={14} aria-hidden="true" />
                            {t('gest:planification.grid.addEmployee')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Legende des periodes */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-meta text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="neutral">’25</Badge> {t('gest:planification.grid.legendPast')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="brand">{t('gest:planification.grid.currentBadge')}</Badge>{' '}
          {t('gest:planification.grid.legendCurrent')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="warning">{t('gest:planification.grid.hgBadge')}</Badge>{' '}
          {t('gest:planification.grid.legendHg')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="warning">{t('gest:planification.grid.legendScenarioBadge')}</Badge>{' '}
          {t('gest:planification.grid.legendScenario')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="info">{t('gest:planification.grid.legendFictifBadge')}</Badge>{' '}
          {t('gest:planification.grid.legendFictif')}
        </span>
      </div>
    </div>
  );
}

export function MarkerBadge({ marker, virtual }: { marker: PeriodMarker; virtual: boolean }) {
  const { t } = useTranslation();
  if (marker.kind === 'past') {
    return (
      <Badge
        tone="neutral"
        title={
          marker.source === 'manual'
            ? t('gest:planification.grid.marker.pastManual', { year: marker.year })
            : t('gest:planification.grid.marker.pastHistory', { year: marker.year })
        }
      >
        <span
          className={cn(marker.source === 'manual' && 'underline decoration-dotted underline-offset-2')}
        >
          ’{String(marker.year % 100).padStart(2, '0')}
        </span>
      </Badge>
    );
  }
  if (marker.kind === 'current') {
    if (marker.offGrid) {
      return (
        <Badge tone="warning" title={t('gest:planification.grid.marker.hgTitle')}>
          {t('gest:planification.grid.hgBadge')}
        </Badge>
      );
    }
    return (
      <Badge tone="brand" title={t('gest:planification.grid.marker.currentTitle')}>
        {t('gest:planification.grid.currentBadge')}
      </Badge>
    );
  }
  return (
    <Badge tone={virtual ? 'info' : 'warning'} title={t('gest:planification.grid.marker.scenarioTitle')}>
      {t('gest:planification.grid.marker.scenarioBadge')}
    </Badge>
  );
}
